import { format, formatDistanceToNowStrict, isValid, parseISO } from 'date-fns'

/**
 * Display formatting.
 *
 * Dates arrive from the API already converted to the tenant's timezone and
 * carrying an offset, so they are rendered as-is rather than re-converted to
 * the browser's zone. An HR manager travelling should still see the deadline
 * the officer at the plant sees.
 */

function toDate(value) {
  if (!value) return null

  const date = typeof value === 'string' ? parseISO(value) : new Date(value)

  return isValid(date) ? date : null
}

export function formatDate(value, fallback = '—') {
  const date = toDate(value)
  return date ? format(date, 'd MMM yyyy') : fallback
}

export function formatDateTime(value, fallback = '—') {
  const date = toDate(value)
  return date ? format(date, 'd MMM yyyy, h:mm a') : fallback
}

export function formatTime(value, fallback = '—') {
  const date = toDate(value)
  return date ? format(date, 'h:mm a') : fallback
}

/** "3 days ago" / "in 4 hours". */
export function formatRelative(value, fallback = '—') {
  const date = toDate(value)
  if (!date) return fallback

  const suffix = date.getTime() > Date.now() ? 'in ' : ''
  const distance = formatDistanceToNowStrict(date)

  return suffix ? `${suffix}${distance}` : `${distance} ago`
}

/**
 * How long until a deadline, or how far past it.
 *
 * Returns the tone as well as the text so a caller does not have to re-derive
 * whether a number is good or bad. `hours` is negative once overdue, which is
 * how the API reports it.
 */
export function formatDeadline(hoursRemaining) {
  if (hoursRemaining === null || hoursRemaining === undefined) {
    return { text: '—', tone: 'neutral' }
  }

  const overdue = hoursRemaining < 0
  const magnitude = Math.abs(hoursRemaining)

  let text
  if (magnitude < 1) {
    const minutes = Math.max(1, Math.round(magnitude * 60))
    text = `${minutes} min`
  } else if (magnitude < 48) {
    text = `${Math.round(magnitude)} hr`
  } else {
    text = `${Math.round(magnitude / 24)} days`
  }

  if (overdue) return { text: `${text} overdue`, tone: 'danger' }
  if (hoursRemaining <= 24) return { text: `${text} left`, tone: 'warning' }

  return { text: `${text} left`, tone: 'neutral' }
}

/** Indian digit grouping — 1,20,000 rather than 120,000. */
export function formatNumber(value, fallback = '—') {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return fallback

  return new Intl.NumberFormat('en-IN').format(value)
}

export function formatPercent(value, fallback = '—', digits = 1) {
  if (value === null || value === undefined) return fallback

  return `${Number(value).toFixed(digits)}%`
}

/**
 * Locale to format each supported currency's digit grouping and symbol
 * placement in, since a currency code alone does not imply one — SAR read as
 * en-IN would group and place its symbol the Indian way instead of how a
 * Saudi reader expects it.
 */
const CURRENCY_LOCALES = {
  INR: 'en-IN', NPR: 'en-IN', BDT: 'en-BD', LKR: 'en-LK', PKR: 'en-PK',
  AED: 'en-AE', SAR: 'en-SA', QAR: 'en-QA', OMR: 'en-OM', KWD: 'en-KW', BHD: 'en-BH',
  MYR: 'en-MY', SGD: 'en-SG', GBP: 'en-GB', USD: 'en-US', AUD: 'en-AU',
}

/**
 * `currency` is the tenant's own configured code (Company Settings) — the
 * caller passes `tenant?.currency`, and this only falls back to INR when
 * that is genuinely absent, so a non-Indian tenant's amounts read in ITS
 * currency rather than always in rupees.
 */
export function formatCurrency(value, currency = 'INR', fallback = '—') {
  if (value === null || value === undefined) return fallback

  return new Intl.NumberFormat(CURRENCY_LOCALES[currency] ?? 'en-IN', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(value)
}

/**
 * Hours as a human duration.
 *
 * Resolution times run from minutes to weeks, and "103.5 hours" is a number
 * nobody converts in their head while reading a dashboard.
 */
export function formatDuration(hours, fallback = '—') {
  if (hours === null || hours === undefined) return fallback

  if (hours < 1) return `${Math.round(hours * 60)} min`
  if (hours < 24) return `${Math.round(hours * 10) / 10} hr`

  const days = hours / 24
  if (days < 14) return `${Math.round(days * 10) / 10} days`

  return `${Math.round(days / 7)} weeks`
}

/** "Overtime for July not credited" → sentence-cased label from a snake_case key. */
export function humanise(value, fallback = '—') {
  if (!value) return fallback

  const spaced = String(value).replace(/[_-]+/g, ' ').trim()

  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

export function initials(name) {
  if (!name) return '?'

  return String(name)
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
}

export function truncate(text, length = 80) {
  if (!text) return ''
  const clean = String(text)

  return clean.length > length ? `${clean.slice(0, length).trimEnd()}…` : clean
}
